/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import type { HomrVisualAsset, HomrVisualAssets } from '@shared/data-access/interfaces';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { LazyLoadDirective } from '@shared/utils/directives';
import { TaggingService } from '@shared/data-access/services';

@Component({
  selector: 'shared-media-component-homr-asset',
  templateUrl: './media-homr-asset.component.html',
  providers: [TaggingService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LazyLoadDirective],
})
export class MediaHomrAssetComponent {
  private readonly taggingService = inject(TaggingService);

  readonly visualAssets = input<[HomrVisualAssets] | undefined>(undefined);
  readonly itemTitle = input<string>('');
  readonly imageWidthParam = input<number>(DCSG_CONSTANTS.maxAppWidth);
  readonly altText = input<string>('');
  readonly willLazyLoad = input<boolean>();
  readonly prioritize = input(false);

  readonly extractedAssets = computed(() => {
    const assets = this.visualAssets()?.[0];

    if (!assets) {
      return {};
    }

    return Object.entries(assets).reduce(
      (prev, [key, asset]) => {
        if (key === 'length' || typeof asset !== 'object') {
          return prev;
        }

        switch (asset?.type) {
          case 'imageUpload':
            return { ...prev, image: this.updateAssetAltText(asset) };
          case 'mobileImageUpload':
            return { ...prev, mobileImage: this.updateAssetAltText(asset) };
          case 'animationUpload':
            return { ...prev, animation: this.updateAssetAltText(asset) };
          default:
            return prev;
        }
      },
      {} as { image?: HomrVisualAsset; mobileImage?: HomrVisualAsset; animation?: HomrVisualAsset },
    );
  });

  readonly image = computed(() => this.extractedAssets().image);
  readonly mobileImageSrcset = computed(() =>
    this.encodeSrcset(this.extractedAssets().mobileImage?.value ?? ''),
  );

  readonly animation = computed(() => this.extractedAssets().animation);
  readonly desktopImageSrcset = computed(() =>
    this.encodeSrcset(this.extractedAssets().image?.value ?? ''),
  );

  private updateAssetAltText(asset: HomrVisualAsset): HomrVisualAsset {
    return {
      ...asset,
      altText: this.taggingService.altTag(asset.altText || this.altText(), this.itemTitle()),
    };
  }

  private encodeSrcset(url: string): string {
    try {
      return new URL(url).href;
    } catch {
      return url;
    }
  }
}
